import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import type { ArticleListItem } from "@/types/api";
import { FreshnessBadge } from "@/components/news/FreshnessBadge";
import { cn } from "@/lib/utils";

interface BreakingNewsBarProps {
  items: ArticleListItem[];
  /** Intervalo de rotação das manchetes (ms) */
  intervalMs?: number;
}

export function BreakingNewsBar({ items, intervalMs = 5000 }: BreakingNewsBarProps) {
  const headlines = items.slice(0, 8);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  const headlineKey = headlines.map((item) => item.id).join(",");

  useEffect(() => {
    setIndex(0);
  }, [headlineKey]);

  useEffect(() => {
    if (headlines.length < 2) return;
    const timer = window.setInterval(() => {
      setVisible(false);
      window.setTimeout(() => {
        setIndex((current) => (current + 1) % headlines.length);
        setVisible(true);
      }, 180);
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [headlines, intervalMs]);

  const headline = headlines[index];
  if (!headline) return null;

  return (
    <div className="bg-brand-chrome text-white shadow-[inset_0_-1px_0_rgba(0,0,0,0.12)]">
      <div className="portal-container flex min-h-10 items-center gap-3 py-2 text-sm">
        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-sm bg-brand-tertiary px-2 py-0.5 font-display text-xs font-bold uppercase tracking-wider">
          <span className="freshness-pulse h-1.5 w-1.5 rounded-full bg-white" aria-hidden />
          Plantao
        </span>

        <div
          className={cn(
            "flex min-w-0 flex-1 items-center gap-2 transition-opacity duration-200 ease-out",
            visible ? "opacity-100" : "opacity-0",
          )}
        >
          <FreshnessBadge
            article={headline}
            className="hidden shrink-0 sm:inline-flex"
            fallbackRelative
            tone="onDark"
          />
          <Link
            to={`/materia/${headline.slug}`}
            className="min-w-0 flex-1 truncate font-medium hover:underline"
          >
            {headline.title}
          </Link>
        </div>

        <Link
          to="/busca?ordering=-publish_at"
          className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold uppercase hover:underline"
        >
          Ver mais
          <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}
